import {
  EntityTagEvent,
  EntityTagEvent_EventType,
  TagExistReason,
} from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';

import type {
  EnumMeta,
  HistorySegment,
  HistorySummary,
  Scale,
  TimedEvent,
  TimelinePosition,
} from './entityTagHistory.types';

export const getEventTime = (event: EntityTagEvent) =>
  event.setTime ? Date.parse(event.setTime) : NaN;

// Returns events that have a valid timestamp, oldest first.
export function sortEventsChronologically(events: EntityTagEvent[]): TimedEvent[] {
  return events
    .map((event) => ({ ...event, time: getEventTime(event) }))
    .filter((event) => Number.isFinite(event.time))
    .sort((a, b) => a.time - b.time);
}

/*
  Each event's `tagExists` describes the tag state from that event until the next one.
  When history is incomplete, the period after the last loaded event is unknown.
 */
export function buildSegments(
  events: TimedEvent[],
  now: number,
  isComplete: boolean
): HistorySegment[] {
  const segments: HistorySegment[] = events.map((event, index) => {
    const next = events[index + 1];
    const isLast = !next;
    return {
      start: event.time,
      end: isLast ? (isComplete ? Math.max(now, event.time) : event.time) : next.time,
      tagged: event.tagExists ?? false,
      event,
    };
  });

  const last = events[events.length - 1];
  if (!isComplete && last && now > last.time) {
    segments.push({ start: last.time, end: now, tagged: undefined });
  }

  return segments;
}

export function summarizeHistory(events: TimedEvent[]): HistorySummary {
  const latest = events[events.length - 1];

  let stateChanges = 0;
  let lastStateChange = events[0];
  events.forEach((event, index) => {
    if (index > 0 && !!event.tagExists !== !!events[index - 1].tagExists) {
      stateChanges += 1;
      lastStateChange = event;
    }
  });

  return {
    totalEvents: events.length,
    stateChanges,
    latestState: latest ? !!latest.tagExists : undefined,
    lastStateChange,
    uniqueActors: new Set(events.map((e) => e.actorId).filter(Boolean)).size,
  };
}

/** Maps each event to a 0–100 position along the timeline. */
export function getPositions(
  segments: HistorySegment[],
  scale: Scale
): TimelinePosition[] {
  if (!segments.length) return [];

  if (scale === 'even') {
    const width = 100 / segments.length;
    return segments.map((_, index) => ({ left: index * width, width }));
  }

  const start = segments[0].start;
  const span = segments[segments.length - 1].end - start;
  if (span <= 0) {
    const width = 100 / segments.length;
    return segments.map((_, index) => ({ left: index * width, width }));
  }

  return segments.map((segment) => ({
    left: ((segment.start - start) / span) * 100,
    width: ((segment.end - segment.start) / span) * 100,
  }));
}

export const eventTypeMeta: Record<string, EnumMeta> = {
  [EntityTagEvent_EventType.ADDED]: {
    label: 'Explicitly added',
    tone: 'explicit',
    direction: 'up',
    description:
      'Someone tagged this entity directly, independent of the rollout percentage.',
  },
  [EntityTagEvent_EventType.REMOVED]: {
    label: 'Explicitly removed',
    tone: 'explicit',
    direction: 'down',
    description: 'Someone removed the direct (explicit) tag from this entity.',
  },
  [EntityTagEvent_EventType.ADDED_PERCENT_BUCKET]: {
    label: 'Rollout: included',
    tone: 'primary',
    direction: 'up',
    description:
      "The tag's rollout percentage was changed and this entity was inside the rollout afterwards.",
  },
  [EntityTagEvent_EventType.REMOVED_PERCENT_BUCKET]: {
    label: 'Rollout: excluded',
    tone: 'primary',
    direction: 'down',
    description:
      "The tag's rollout percentage was changed and this entity was outside the rollout afterwards.",
  },
  [EntityTagEvent_EventType.UNKNOWN]: {
    label: 'Unknown',
    tone: 'neutral',
    direction: 'none',
    description: 'The API reported an unknown event type.',
  },
};

export const reasonMeta: Record<string, EnumMeta> = {
  [TagExistReason.ENTITY_EXPLICITLY_TAGGED]: {
    label: 'Explicitly tagged',
    tone: 'explicit',
    description: 'Tagged because of a direct (explicit) tag on this entity.',
  },
  [TagExistReason.ENTITY_IN_TAG_PERCENT]: {
    label: 'In rollout percentage',
    tone: 'primary',
    description: "Tagged because the entity falls inside the tag's rollout percentage.",
  },
  [TagExistReason.ENTITY_NOT_TAGGED]: {
    label: 'No tag applies',
    tone: 'neutral',
    description: 'Neither an explicit tag nor the rollout percentage applies.',
  },
  [TagExistReason.TAG_EXIST_REASON_UNSPECIFIED]: {
    label: 'Unspecified',
    tone: 'neutral',
    description: 'The API did not specify a reason.',
  },
};

const fallbackMeta = (raw?: string): EnumMeta => ({
  label: raw ?? 'Not provided',
  tone: 'neutral',
  direction: 'none',
  description: raw ? 'Value not recognized by this UI.' : 'Not provided by the API.',
});

export const getEventTypeMeta = (eventType?: string): EnumMeta =>
  (eventType && eventTypeMeta[eventType]) || fallbackMeta(eventType);

export const getReasonMeta = (reason?: string): EnumMeta =>
  (reason && reasonMeta[reason]) || fallbackMeta(reason);
